import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { Search, RefreshCw, Plus } from "lucide-react"
import { api } from "@/lib/api"
import { PageHeader } from "@/components/portal/PageHeader"
import { FilterBar } from "@/components/portal/FilterBar"
import { Loading, ErrorBox } from "@/components/portal/LoadingError"
import { toast } from "@/components/portal/Toast"
import { ContactEditModal } from "@/components/portal/ContactEditModal"
import { CreateContactModal } from "@/components/portal/CreateContactModal"
import { useTenantFilter } from "@/hooks/useTenantFilter"

async function fetchContacts(tenant: string, q: string) {
  const { data, error } = await api.GET("/api/v1/contacts", {
    params: { query: { tenant: tenant || undefined, q: q || undefined, limit: 500 } },
  })
  if (error) throw new Error(String(error))
  return data
}

function fmtSync(iso: string | null | undefined): string {
  if (!iso) return "noch nie"
  return iso.slice(0, 16).replace("T", " ")
}

/** Kontakte-Liste 1:1 wie admin/contacts.py. */
export default function ContactsList() {
  const [tenant, setTenant] = useTenantFilter()
  const [q, setQ] = useState("")
  const [editId, setEditId] = useState<number | null>(null)
  const [editTenant, setEditTenant] = useState("")
  const [createOpen, setCreateOpen] = useState(false)
  const qc = useQueryClient()
  const { data, isLoading, error, isFetching, refetch } = useQuery({
    queryKey: ["contacts", tenant, q],
    queryFn: () => fetchContacts(tenant, q),
  })

  const syncNow = useMutation({
    mutationFn: async () => {
      const r = await fetch("/admin/contacts/sync", { method: "POST", credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contacts"] })
      toast("✓ Kontakte-Sync läuft")
    },
    onError: (e) => toast("Sync fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error"),
  })

  if (error) return <div className="pp-main"><ErrorBox error={error} /></div>

  const items = data?.items || []

  return (
    <>
      <PageHeader title="Kontakte" count={data?.total} onRefresh={() => refetch()} refreshing={isFetching}>
        <button
          type="button"
          onClick={() => {
            if (!tenant) {
              toast("Bitte zuerst einen Tenant auswählen", "error")
              return
            }
            setCreateOpen(true)
          }}
          className="pp-btn pp-btn-sm pp-btn-primary"
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <Plus className="w-3 h-3" />
          Neuer Kontakt
        </button>
      </PageHeader>

      <CreateContactModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        mode="portal"
        tenantSlug={tenant || ""}
        itemId={0}
        onSuccess={() => {
          qc.invalidateQueries({ queryKey: ["contacts"] })
          refetch()
        }}
      />

      <FilterBar tenant={tenant} onTenantChange={setTenant}>
        <div style={{ position: "relative", marginLeft: "auto" }}>
          <Search style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", width: 14, height: 14 }} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Name / E-Mail / Telefon..."
            style={{ width: 260, paddingLeft: 28, paddingRight: 10, height: 30, fontSize: 13 }}
          />
        </div>
      </FilterBar>

      <div style={{ marginTop: 10, fontSize: 12, color: "var(--text-muted)", display: "flex", gap: 10, alignItems: "center" }}>
        <span>{data?.total_cached ?? 0} Kontakte im Cache</span>
        <span>·</span>
        <span>Letzter Sync: {fmtSync(data?.last_sync)}</span>
        <span>·</span>
        <button
          type="button"
          onClick={() => syncNow.mutate()}
          disabled={syncNow.isPending}
          style={{ background: "none", border: "none", color: "var(--accent-color)", cursor: "pointer", padding: 0, fontSize: 12, textDecoration: "underline" }}
        >
          <RefreshCw className={syncNow.isPending ? "animate-spin inline" : "inline"} style={{ width: 11, height: 11, marginRight: 3 }} />
          {syncNow.isPending ? "Synchronisiere..." : "Jetzt synchronisieren"}
        </button>
      </div>

      {!tenant ? (
        <div className="pp-card" style={{ padding: 48, textAlign: "center", color: "var(--text-muted)" }}>
          Bitte einen Tenant wählen, um Kontakte anzuzeigen.
        </div>
      ) : isLoading ? (
        <Loading label="Lädt Kontakte..." />
      ) : items.length === 0 ? (
        <div className="pp-card" style={{ padding: 48, textAlign: "center", color: "var(--text-muted)" }}>
          Keine Kontakte gefunden
        </div>
      ) : (
        <>
          <div className="pp-card" style={{ padding: 0, marginBottom: 8 }}>
            <div className="pp-tbl-wrap" style={{ border: "none", boxShadow: "none", margin: 0 }}>
              <table>
                <thead>
                  <tr>
                    <th>Nr.</th>
                    <th>Name</th>
                    <th className="col-hide-md">Telefon</th>
                    <th className="col-hide-md">E-Mail</th>
                    <th className="col-hide-lg">Adresse</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((c) => {
                    const name = [c.first_name, c.last_name].filter(Boolean).join(" ") || c.company_name || "—"
                    const phone = c.phone_mobile || c.phone_home || ""
                    const addr = [c.street, [c.postcode, c.city].filter(Boolean).join(" ")].filter(Boolean).join(", ") || "—"
                    const openEdit = () => {
                      setEditId(c.id)
                      setEditTenant(c.kunden_slug || tenant)
                    }
                    return (
                      <tr
                        key={c.id}
                        style={{ cursor: "pointer" }}
                        title="Klicken zum Bearbeiten"
                        onClick={openEdit}
                      >
                        <td style={{ fontWeight: 500, color: "var(--accent-color)" }}>
                          {(c as any).nr || c.id}
                        </td>
                        <td data-pii>{name}</td>
                        <td className="col-hide-md" data-pii onClick={(e) => e.stopPropagation()}>
                          {phone ? <a href={`tel:${phone}`} style={{ color: "var(--accent-color)" }}>{phone}</a> : "—"}
                        </td>
                        <td className="col-hide-md" data-pii>{c.email || "—"}</td>
                        <td className="col-hide-lg" data-pii>{addr}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", textAlign: "right", marginTop: 8 }}>
            {items.length} Kontakte angezeigt{items.length >= 500 ? " (max. 500)" : ""}
          </div>
        </>
      )}

      <ContactEditModal
        open={editId !== null}
        onClose={() => setEditId(null)}
        contactId={editId}
        tenantSlug={editTenant}
        invalidateKeys={[["contacts"]]}
      />
    </>
  )
}
